import React, {
  useState, useCallback, useMemo, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Disclosure, Link } from 'anf-core-react';

function LegalPolicyLeftNavContainer({ navData = {}, textKey: initialTextKey = '' }) {
  const legalPolicyLinkMap = useMemo(
    () => ({
      LEGAL_ABOUT_US: 'aboutUsLink',
      LEGAL_CA_PRIVACY_POLICY: 'caPrivacyPolicy',
      LEGAL_CLUB_TERMS: 'clubTerms',
      LEGAL_COOKIES_POLICY: 'aboutCookies',
      LEGAL_PRIVACY_POLICY: 'privacyPolicy',
      LEGAL_SALES_TERMS: 'salesTerms',
      LEGAL_SITE_USE: 'termsOfUse',
      LEGAL_SOCIAL_MEDIA_POLICY: 'socialMediaEngagement',
      LEGAL_TEXTS_TERMS: 'textTerms',
      LEGAL_BRAND_PROTECTION: 'brandprotection',
      LEGAL_MODERN_SLAVERY_ACT: 'ukSlaveryAct',
      LEGAL_POLICY_CA_CLEANING_PRODUCT: 'caCleaningProductAct',
      LEGAL_POLICY_PERSONAL_INFO: 'personalInfo',
      GLB_COMPANY_INFO_TXT: 'companyInfo',
    }),
    [],
  );

  const textKey = legalPolicyLinkMap[initialTextKey] || initialTextKey;

  const hasSelectedChild = useCallback(
    (children) => Array.isArray(children) && children.some((childKey) => childKey === textKey),
    [textKey],
  );

  const initialRender = useRef(true);
  const userInteractions = useRef({});

  const [expandedSections, setExpandedSections] = useState(() => {
    if (!navData) return {};

    // eslint-disable-next-line no-unused-vars
    return Object.entries(navData || {}).reduce((state, [_, section]) => {
      if (section && section.children && hasSelectedChild(section.children)) {
        return { ...state, [section.id]: true };
      }
      return state;
    }, {});
  });

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    }
  }, []);

  const renderNavLink = useCallback(
    ({
      id, url, title, newWindow,
    }, navItemKey) => {
      const isSelected = navItemKey === textKey;
      return (
        <Link
          href={url}
          target={newWindow ? '_blank' : '_self'}
          rel={newWindow ? 'noopener noreferrer' : undefined}
          data-selected={isSelected}
          data-testid={`legal-policy-link-${id}`}
        >
          <div className={`legal-policy-link ${isSelected ? 'selected' : ''}`}>
            {title}
          </div>
        </Link>
      );
    },
    [textKey],
  );

  const renderNavItem = useCallback(
    (navItemKey, isChild = false) => {
      if (!navData || !navItemKey) return null;

      const navItem = navData[navItemKey];
      if (!navItem || !navItem.displayable?.legalPolicy) return null;

      return (
        <li key={navItem.id} className={`legal-policy-nav-navItem ${isChild ? 'child' : 'parent'}`}>
          {renderNavLink(navItem, navItemKey)}
        </li>
      );
    },
    [navData, renderNavLink],
  );

  const renderSection = useCallback(
    (navSectionKey) => {
      if (!navData || !navSectionKey) return null;

      const section = navData[navSectionKey];
      if (!section || !section.displayable?.legalPolicy) return null;

      const hasActiveChild = Array.isArray(section.children)
        && section.children.some((childKey) => childKey === textKey);
      const isUserInteracted = userInteractions.current[section.id];

      let isExpanded;
      if (isUserInteracted) {
        isExpanded = !!expandedSections[section.id];
      } else {
        isExpanded = hasActiveChild || !!expandedSections[section.id];
      }

      const handleToggle = () => {
        if (!isUserInteracted && hasActiveChild) {
          userInteractions.current[section.id] = true;
          setExpandedSections((prev) => ({
            ...prev,
            [section.id]: false,
          }));
        } else {
          userInteractions.current[section.id] = true;
          setExpandedSections((prev) => ({
            ...prev,
            [section.id]: !prev[section.id],
          }));
        }
      };

      return (
        <li key={section.id} className="legal-policy-section">
          <Disclosure
            id={`legal-policy-section-${section.id}`}
            isExpanded={isExpanded}
            onClick={handleToggle}
            label={section.title}
          >
            {Array.isArray(section.children) && section.children.length > 0 && (
              <ul className="legal-policy-section-children">
                {section.children.map((childKey) => renderNavItem(childKey, true)).filter(Boolean)}
              </ul>
            )}
          </Disclosure>
        </li>
      );
    },
    [navData, expandedSections, setExpandedSections, renderNavItem, textKey],
  );

  const navSections = useMemo(
    () => [
      'privacySection',
      'legalAboutUsLinkSection',
      'californiaDisclosuresSection',
      'externalLinkSection',
    ],
    [],
  );

  if (!navData) {
    return null;
  }

  return (
    <nav className="legal-policy-nav" data-testid="legal-policy-nav">
      <ul className="legal-policy-nav-list">
        {navSections.map((navSectionKey) => renderSection(navSectionKey)).filter(Boolean)}
      </ul>
    </nav>
  );
}

LegalPolicyLeftNavContainer.propTypes = {
  navData: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      children: PropTypes.arrayOf(PropTypes.string),
      displayable: PropTypes.shape({
        legalPolicy: PropTypes.bool,
      }),
    }),
  ),
  textKey: PropTypes.string,
};

LegalPolicyLeftNavContainer.defaultProps = {
  navData: {},
  textKey: '',
};

export default LegalPolicyLeftNavContainer;
